import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/system";
import { Skeleton } from "@mui/material";
import NegativeBalanceFilter from "./NegativeBalanceFilter";
import NegativeBalanceItem from "./NegativeBalanceItem";
import {
  DEFAULT_NEGATIVE_BALANCE_SORT,
  sortNegativeBalanceRows,
} from "./negativeBalanceSort";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import {
  selectBizMobileBeAwareNegativeBalanceLoading,
  selectBizMobileBeAwareNegativeBalanceOutput,
} from "../../../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { exportBeAwareTableToCSV } from "../../../../../../DashboardDesktop/BeAware/beAwareCsvHelpers";

// CSV column order/titles — identical to Negative Balance Desktop.
const tableHeadings = {
  brand_name: "Brand Name",
  brand_id: "Brand ID",
  total_income: "Total Income",
  calculated_total_income: "Calculated Total Income",
  cpa_income: "CPA Income",
  rs_income: "RS Income",
  last_month_balance: "Last Month's End Balance",
  brand_policy_plan_name: "Brand Policy Plan Name",
  qftd: "QFTD",
  ftd: "FTD",
  regs: "Regs",
  click_out: "Click Outs",
};

// Keep raw values; empty string when missing so the CSV shows a blank cell.
const raw = (value: any) =>
  value === null || value === undefined ? "" : String(value);

// Round monetary values to 2 decimals; empty string when missing/non-numeric.
const money = (value: any) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : String(Math.round(n * 100) / 100);
};

// Monetary columns prefixed with "$" in the CSV (mirrors Desktop).
const MONEY_KEYS = new Set([
  "total_income",
  "calculated_total_income",
  "cpa_income",
  "rs_income",
  "last_month_balance",
]);

// CSV cell resolver: prefix monetary columns with "$" to match Desktop.
const getNegativeBalanceCellValue = (key: string, row: any) => {
  const value = row[key] ?? "";
  return MONEY_KEYS.has(key) && value !== "" ? `$${value}` : value;
};

const NegativeBalanceSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Stack spacing={2}>
      {[1, 2, 3].map((item) => (
        <Box
          key={item}
          sx={{ borderRadius: 2, border: "1px solid #e0e0e0", p: 2 }}
        >
          <Stack spacing={1.2}>
            <Skeleton width={140} height={20} />
            <Stack direction="row" justifyContent="space-between">
              <Skeleton width={160} height={18} />
              <Skeleton width={80} height={24} />
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Skeleton width={120} height={18} />
              <Skeleton width={120} height={18} />
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  </Box>
);

const NegativeBalance = () => {
  const [searchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");

  const isLoading = useSelector(selectBizMobileBeAwareNegativeBalanceLoading);
  const output = useSelector(selectBizMobileBeAwareNegativeBalanceOutput);
  // const output = [
  //   {
  //     brand_names: "Bettom Sport UK",
  //     brand_id: "2319",
  //     calculate_total_income: -151577,
  //     balance_end_last_month: -48200,
  //     totalincome_dollar: 103509,
  //     cpa_dollar: 29116,
  //     revshare_dollar: 6689,
  //     clicks: 1751,
  //     reg: 515,
  //     ftd: 500,
  //     qftd: 420,
  //     brand_policy_plan_name: "Brand policy example",
  //   },
  //   {
  //     brand_names: "Casino Royale DE",
  //     brand_id: "4487",
  //     calculate_total_income: -487048,
  //     balance_end_last_month: -120340,
  //     totalincome_dollar: 254890,
  //     cpa_dollar: 61200,
  //     revshare_dollar: 18450,
  //     clicks: 3902,
  //     reg: 1120,
  //     ftd: 980,
  //     qftd: 875,
  //     brand_policy_plan_name: "Standard CPA + RS",
  //   },
  //   {
  //     brand_names: "Lucky Spins IT",
  //     brand_id: "7756",
  //     calculate_total_income: 32410,
  //     balance_end_last_month: 15600,
  //     totalincome_dollar: 88750,
  //     cpa_dollar: 14200,
  //     revshare_dollar: 4310,
  //     clicks: 640,
  //     reg: 210,
  //     ftd: 185,
  //     qftd: 160,
  //     brand_policy_plan_name: "RevShare only",
  //   },
  //   {
  //     brand_names: "Golden Bet ES",
  //     brand_id: "9021",
  //     calculate_total_income: -9800,
  //     balance_end_last_month: null,
  //     totalincome_dollar: null,
  //     cpa_dollar: 0,
  //     revshare_dollar: 0,
  //     clicks: 0,
  //     reg: 0,
  //     ftd: 0,
  //     qftd: 0,
  //     brand_policy_plan_name: null,
  //   },
  //   {
  //     brand_names: "Royal Win UK",
  //     brand_id: "1135",
  //     calculate_total_income: -68320,
  //     balance_end_last_month: -28400,
  //     totalincome_dollar: 145890,
  //     cpa_dollar: 35210,
  //     revshare_dollar: 9785,
  //     clicks: 2185,
  //     reg: 684,
  //     ftd: 590,
  //     qftd: 512,
  //     brand_policy_plan_name: "Hybrid CPA + RS",
  //   },
  //   {
  //     brand_names: "Spin Masters CA",
  //     brand_id: "5542",
  //     calculate_total_income: -214980,
  //     balance_end_last_month: -78560,
  //     totalincome_dollar: 198420,
  //     cpa_dollar: 48100,
  //     revshare_dollar: 15260,
  //     clicks: 3120,
  //     reg: 890,
  //     ftd: 755,
  //     qftd: 684,
  //     brand_policy_plan_name: "Premium CPA",
  //   },
  //   {
  //     brand_names: "Vegas Legends",
  //     brand_id: "7810",
  //     calculate_total_income: 98540,
  //     balance_end_last_month: 42580,
  //     totalincome_dollar: 267350,
  //     cpa_dollar: 72140,
  //     revshare_dollar: 22380,
  //     clicks: 4520,
  //     reg: 1295,
  //     ftd: 1104,
  //     qftd: 982,
  //     brand_policy_plan_name: "Revenue Share",
  //   },
  //   {
  //     brand_names: "Ace Gaming",
  //     brand_id: "3217",
  //     calculate_total_income: -45120,
  //     balance_end_last_month: -18400,
  //     totalincome_dollar: 112680,
  //     cpa_dollar: 25400,
  //     revshare_dollar: 7850,
  //     clicks: 1542,
  //     reg: 438,
  //     ftd: 372,
  //     qftd: 328,
  //     brand_policy_plan_name: "Standard CPA",
  //   },
  //   {
  //     brand_names: "Jackpot Heroes",
  //     brand_id: "8409",
  //     calculate_total_income: -126500,
  //     balance_end_last_month: -54820,
  //     totalincome_dollar: 187450,
  //     cpa_dollar: 44680,
  //     revshare_dollar: 13540,
  //     clicks: 2810,
  //     reg: 804,
  //     ftd: 695,
  //     qftd: 618,
  //     brand_policy_plan_name: "VIP Revenue Plan",
  //   },
  //   {
  //     brand_names: "Fortune Casino",
  //     brand_id: "6024",
  //     calculate_total_income: 56890,
  //     balance_end_last_month: 22150,
  //     totalincome_dollar: 154730,
  //     cpa_dollar: 31240,
  //     revshare_dollar: 9640,
  //     clicks: 1938,
  //     reg: 584,
  //     ftd: 491,
  //     qftd: 432,
  //     brand_policy_plan_name: "CPA + RevShare",
  //   },
  //   {
  //     brand_names: "Bet Planet",
  //     brand_id: "4588",
  //     calculate_total_income: -305600,
  //     balance_end_last_month: -99820,
  //     totalincome_dollar: 342100,
  //     cpa_dollar: 91800,
  //     revshare_dollar: 26400,
  //     clicks: 5265,
  //     reg: 1498,
  //     ftd: 1325,
  //     qftd: 1196,
  //     brand_policy_plan_name: "Enterprise CPA",
  //   },
  //   {
  //     brand_names: "Super Slots",
  //     brand_id: "2674",
  //     calculate_total_income: -18540,
  //     balance_end_last_month: -8420,
  //     totalincome_dollar: 76320,
  //     cpa_dollar: 15860,
  //     revshare_dollar: 4920,
  //     clicks: 874,
  //     reg: 276,
  //     ftd: 214,
  //     qftd: 196,
  //     brand_policy_plan_name: "RevShare Basic",
  //   },
  //   {
  //     brand_names: "Spin Kingdom",
  //     brand_id: "9981",
  //     calculate_total_income: 143250,
  //     balance_end_last_month: 61820,
  //     totalincome_dollar: 352460,
  //     cpa_dollar: 86450,
  //     revshare_dollar: 28510,
  //     clicks: 6180,
  //     reg: 1782,
  //     ftd: 1520,
  //     qftd: 1386,
  //     brand_policy_plan_name: "High Volume CPA",
  //   },
  //   {
  //     brand_names: "Lucky Panda",
  //     brand_id: "3746",
  //     calculate_total_income: -74200,
  //     balance_end_last_month: -31840,
  //     totalincome_dollar: 138920,
  //     cpa_dollar: 29640,
  //     revshare_dollar: 8840,
  //     clicks: 2074,
  //     reg: 612,
  //     ftd: 548,
  //     qftd: 491,
  //     brand_policy_plan_name: "Mixed Revenue Plan",
  //   },
  //   {
  //     brand_names: "Bet Empire",
  //     brand_id: "7103",
  //     calculate_total_income: -12450,
  //     balance_end_last_month: -3620,
  //     totalincome_dollar: 52840,
  //     cpa_dollar: 10650,
  //     revshare_dollar: 2840,
  //     clicks: 612,
  //     reg: 188,
  //     ftd: 145,
  //     qftd: 131,
  //     brand_policy_plan_name: "Starter CPA",
  //   },
  // ];
  const { fetchBeAwareNegativeBalanceData, getSiteFromWorkspace } =
    useBizDevelopmentDashboardMobile();

  // Brand Name search — mirrors Budget Control Card View (substring match).
  const [brandName, setBrandName] = useState("");

  const [sortKey, setSortKey] = useState(DEFAULT_NEGATIVE_BALANCE_SORT.key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_NEGATIVE_BALANCE_SORT.dir,
  );

  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    fetchBeAwareNegativeBalanceData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  // Unique, non-empty brand names used as the search options.
  const brandList = useMemo(() => {
    const set = new Set<string>();
    (output ?? []).forEach((row: Record<string, any>) => {
      const name = String(row?.brand_names ?? "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [output]);

  // Rows shown in the cards (and exported) respect the brand search.
  const filteredOutput = useMemo(() => {
    const search = brandName.trim().toLowerCase();
    if (!search) return output ?? [];
    return (output ?? []).filter((row: Record<string, any>) =>
      String(row?.brand_names ?? "")
        .toLowerCase()
        .includes(search),
    );
  }, [output, brandName]);

  // Sorted API rows shared by the cards and the CSV download.
  const sortedOutput = useMemo(
    () => sortNegativeBalanceRows(filteredOutput, sortKey, sortDir),
    [filteredOutput, sortKey, sortDir],
  );

  // CSV rows — mapped from the same API keys as Negative Balance Desktop.
  const csvRows = useMemo(
    () =>
      sortedOutput.map((item: Record<string, any>) => ({
        brand_name: raw(item.brand_names),
        brand_id: raw(item.brand_id),
        total_income: money(item.totalincome_dollar),
        calculated_total_income: money(item.calculate_total_income),
        cpa_income: money(item.cpa_dollar),
        rs_income: money(item.revshare_dollar),
        last_month_balance: money(item.balance_end_last_month),
        brand_policy_plan_name: raw(item.brand_policy_plan_name),
        qftd: raw(item.qftd),
        ftd: raw(item.ftd),
        regs: raw(item.reg),
        click_out: raw(item.clicks),
      })),
    [sortedOutput],
  );

  const brandCount = filteredOutput.length;

  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
  };

  const handleDownload = () => {
    exportBeAwareTableToCSV({
      tableHeadings,
      rows: csvRows,
      filename: "negative_balance",
      getCellValue: getNegativeBalanceCellValue,
    });
  };

  return (
    <>
      <Box sx={{ px: 2 }}>
        <NegativeBalanceFilter
          brandCount={brandCount}
          onDownload={handleDownload}
          downloadDisabled={isLoading || brandCount === 0}
          sortKey={sortKey}
          sortDir={sortDir}
          onApplySort={handleApplySort}
          brandList={brandList}
          brandName={brandName}
          onBrandNameChange={setBrandName}
        />
        {isLoading ? (
          <NegativeBalanceSkeleton />
        ) : (
          <NegativeBalanceItem data={sortedOutput} />
        )}
      </Box>
    </>
  );
};

export default NegativeBalance;
